/**
 * Client-side estimate of how long a Run Extraction click will take.
 *
 * Mirrors the backend semantics:
 *   - Each chunk is ~60_000 chars (matching `_EXTRACTION_MAX_TOKENS * 4.5`
 *     in `src/libs/temporal/.../phase2/extraction_chunker.py`).
 *   - LLM calls run in parallel up to `KG_MAX_CONCURRENCY` (backend default 10
 *     from `schema_extraction/service.py`).
 *   - Each LLM call takes 35-50s (heuristic).
 *   - Post-processing (entity merge + cleanup + person matching) adds ~30s.
 *
 * For TXT/MD/LOG, `chars` is approximated from `file_size` (UTF-8/ASCII
 * assumption; off by some factor for multibyte text).
 *
 * For PDF/DOCX and IMAGE we cannot derive char count from file_size cheaply,
 * so we return a static placeholder string.
 *
 * For tabular formats (CSV/XLSX/XLS) the estimate panel is suppressed.
 */

const CHARS_PER_CHUNK = 60_000
export const KG_MAX_CONCURRENCY = 10
const SECONDS_PER_CHUNK_MIN = 35
const SECONDS_PER_CHUNK_MAX = 50
const POST_PROCESSING_SECONDS = 30

const TIER_INFO_BOUND = 60
const TIER_AMBER_BOUND = 300
const TIER_RED_BOUND = 1200

const TEXT_EXTS = new Set(['txt', 'md', 'log'])
const PDF_DOC_EXTS = new Set(['pdf', 'docx', 'doc'])
const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'webp'])
const TABULAR_EXTS = new Set(['csv', 'xlsx', 'xls'])

export type EstimateTier = 'none' | 'info' | 'amber' | 'red'

export type EstimateInputItem = {
  file_type: string | null | undefined
  file_size: number
}

export type EstimateResult = {
  tier: EstimateTier
  show_panel: boolean
  /** Lower bound in seconds. 0 when no precise estimate is available. */
  min_sec: number
  /** Upper bound in seconds. 0 when no precise estimate is available. */
  max_sec: number
  /** Number of chunks contributing to the estimate (text inputs only). */
  chunk_count: number
  /** Number of items contributing to the estimate. */
  item_count: number
  /** Static text shown when no precise computation applies (PDF/DOCX/IMAGE). */
  static_placeholder?: string
}

/**
 * Compute the estimate for the selected documents.
 *
 * Pure — no React, no DOM, no I/O.
 */
export function computeExtractionEstimate(items: EstimateInputItem[]): EstimateResult {
  if (items.length === 0) {
    return {
      tier: 'none',
      show_panel: false,
      min_sec: 0,
      max_sec: 0,
      chunk_count: 0,
      item_count: 0,
    }
  }

  const classified = items.map(item => ({
    ext: (item.file_type ?? '').toLowerCase().replace(/^\./, ''),
    size: Math.max(0, item.file_size ?? 0),
  }))

  const textItems = classified.filter(c => TEXT_EXTS.has(c.ext))
  const pdfDocItems = classified.filter(c => PDF_DOC_EXTS.has(c.ext))
  const imageItems = classified.filter(c => IMAGE_EXTS.has(c.ext))
  const tabularItems = classified.filter(c => TABULAR_EXTS.has(c.ext))

  // Tabular-only selection — no estimate panel.
  const allTabular = tabularItems.length === items.length
  if (allTabular) {
    return {
      tier: 'none',
      show_panel: false,
      min_sec: 0,
      max_sec: 0,
      chunk_count: 0,
      item_count: items.length,
    }
  }

  // If we have any text input, we can compute a precise estimate.
  if (textItems.length > 0) {
    const totalChars = textItems.reduce((sum, t) => sum + t.size, 0)
    const chunks = Math.max(1, Math.ceil(totalChars / CHARS_PER_CHUNK))
    const batches = Math.max(1, Math.ceil(chunks / KG_MAX_CONCURRENCY))
    const minSec = batches * SECONDS_PER_CHUNK_MIN + POST_PROCESSING_SECONDS
    const maxSec = batches * SECONDS_PER_CHUNK_MAX + POST_PROCESSING_SECONDS

    return {
      tier: tierFromMaxSec(maxSec),
      show_panel: true,
      min_sec: minSec,
      max_sec: maxSec,
      chunk_count: chunks,
      item_count: items.length,
    }
  }

  // Otherwise: static placeholder for the dominant non-text type.
  if (pdfDocItems.length >= imageItems.length && pdfDocItems.length > 0) {
    return {
      tier: 'info',
      show_panel: true,
      min_sec: 0,
      max_sec: 0,
      chunk_count: 0,
      item_count: items.length,
      static_placeholder: 'Typically 1–5 minutes for most PDFs',
    }
  }

  if (imageItems.length > 0) {
    return {
      tier: 'info',
      show_panel: true,
      min_sec: 0,
      max_sec: 0,
      chunk_count: 0,
      item_count: items.length,
      static_placeholder: 'Typically 30–90 seconds per image',
    }
  }

  // Unknown types — show a generic info panel without a numeric estimate.
  return {
    tier: 'info',
    show_panel: true,
    min_sec: 0,
    max_sec: 0,
    chunk_count: 0,
    item_count: items.length,
    static_placeholder: 'Processing time depends on file content',
  }
}

function tierFromMaxSec(maxSec: number): EstimateTier {
  if (maxSec < TIER_INFO_BOUND) return 'none'
  if (maxSec < TIER_AMBER_BOUND) return 'info'
  if (maxSec < TIER_RED_BOUND) return 'amber'
  return 'red'
}

/**
 * Format a (min_sec, max_sec) pair as a human-readable range.
 *
 * Examples: "~30–80 seconds", "~2–4 minutes", "~1.5–2.5 hours".
 */
export function formatEstimateRange(minSec: number, maxSec: number): string {
  if (maxSec < TIER_INFO_BOUND) return '~under a minute'
  if (maxSec < TIER_AMBER_BOUND) {
    return `~${formatSecondsAsMinutes(minSec, false)}–${formatSecondsAsMinutes(maxSec, true)}`
  }
  if (maxSec < TIER_RED_BOUND) {
    return `~${formatSecondsAsMinutes(minSec, false)}–${formatSecondsAsMinutes(maxSec, true)}`
  }
  // Long ranges: hours.
  const minH = (minSec / 3600).toFixed(1).replace(/\.0$/, '')
  const maxH = (maxSec / 3600).toFixed(1).replace(/\.0$/, '')
  return `~${minH}–${maxH} hours`
}

function formatSecondsAsMinutes(sec: number, withUnit: boolean): string {
  const mins = sec / 60
  // Round to integer for cleaner display; show "X minute" or "X minutes".
  const rounded = Math.max(1, Math.round(mins))
  if (!withUnit) return String(rounded)
  return `${rounded} ${rounded === 1 ? 'minute' : 'minutes'}`
}
